/**
 * 后端错误信息的三种形状。
 *
 * 1. typed envelope —— `err.response.data.message`（本项目的统一错误体）
 * 2. FastAPI 原生 —— `err.response.data.detail`（string 或 422 的 detail 数组）
 * 3. 被 axios 响应拦截器**翻译过**的 400/422 —— 一个**没有 `.response`** 的
 *    Error 子类，信息只在 `err.message` 上
 *
 * 只认第 1 种会在 422 上显示 "Request failed"，只认前两种会在被拦截器翻译过的
 * 400 上显示 fallback —— 而那正是表单校验最常见的返回码。
 *
 * 422 的 detail 是**数组**，一次提交里每个不合法字段各占一条。只取 `detail[0].msg`
 * 会把后面几条静默吞掉：用户按提示改完第一个字段、再提交，才知道还有第二个 ——
 * 一次校验要走几轮往返。这里把全部条目连起来说完（见 `formatDetailEntries`）。
 */
export function extractApiErrorMessage(err: unknown, fallback: string): string {
  const e = err as {
    response?: { data?: { message?: unknown; detail?: unknown } };
    message?: unknown;
  };

  const message = e?.response?.data?.message;
  if (typeof message === 'string' && message) return message;

  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const joined = formatDetailEntries(detail);
    if (joined) return joined;
  }

  // `err.message` 只在**第 3 种**形状下才是服务端说的话。存在 `.response` 就说明这个
  // 错误没被拦截器翻译过（原始 AxiosError），它的 `.message` 恒为 axios 自己的模板
  // "Request failed with status code NNN" —— body 里没有可读信息时把它当"原因"显示，
  // 用户读到的是一句内部报错（4xx 空 body 的真实情况：权限中间件、网关拦截）。
  if (!e?.response && typeof e?.message === 'string' && e.message) return e.message;
  return fallback;
}

/** 一条 detail 里的「说人话那句」——FastAPI 原生用 `msg`，本项目 handler 用 `message`。 */
function detailMsg(entry: { msg?: unknown; message?: unknown }): string {
  for (const raw of [entry.msg, entry.message]) {
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
  }
  return '';
}

/**
 * 422 detail 数组 → 一行可读文案。
 *
 * 每条尽量带上字段名（`loc` 末段 / `details[].field`，`body.` 这类容器前缀剥掉），
 * 拿不到字段名就只出 msg —— 单字段错误因此与改动前逐字一致，不会平白多出一个
 * "undefined:" 前缀。完全相同的条目去重（Pydantic 对同一字段可能同时报
 * missing + type_error），按原顺序用 `; ` 连接。
 *
 * 字段名**不转 camelCase**：这里是给人读的提示，要跟后端报的名字对得上；
 * 需要拿字段名去定位输入框的走 `extractApiErrorField`（那里才需要 camel）。
 */
function formatDetailEntries(detail: unknown[]): string {
  const lines: string[] = [];
  for (const raw of detail) {
    if (!raw || typeof raw !== 'object') continue;
    const entry = raw as { msg?: unknown; message?: unknown; loc?: unknown; field?: unknown };
    const msg = detailMsg(entry);
    if (!msg) continue;

    let field = '';
    if (typeof entry.field === 'string' && entry.field) {
      field = entry.field;
    } else if (Array.isArray(entry.loc) && entry.loc.length > 0) {
      field = String(entry.loc[entry.loc.length - 1]);
    }
    const last = field.split('.').filter(Boolean).pop() ?? '';
    const label = last && last !== 'body' && last !== 'query' && last !== 'path' ? last : '';

    const line = label ? `${label}: ${msg}` : msg;
    if (!lines.includes(line)) lines.push(line);
  }
  return lines.join('; ');
}

/**
 * 服务端**明确拒绝**了这次请求（4xx），而不是网络 / 超时 / 5xx。
 *
 * 用途：决定「要不要把服务端那句话给用户看」。4xx 的 message 是业务/校验原因
 * （"listing is inactive"），对用户有意义；5xx、网络错误、超时的文案是 axios 自己
 * 的模板（"Request failed with status code 502" / "Network Error"），给用户看只会
 * 制造困惑 —— 那种情况只出通用失败文案。
 *
 * 两种形状都要认，理由同 `isNotFoundError`：拦截器把带 envelope 的 4xx 翻译成
 * **没有 `.response`** 的 Error 子类，状态码挪到了 `err.status` 上。
 *
 * 顺带也是「附件孤儿要不要清理」的判据：4xx = 事务没落库，附件确定没人关联；
 * 网络错误 / 5xx 下请求可能已在服务端成功，删了会删掉真实记录的照片。
 */
export function isClientRejection(err: unknown): boolean {
  const e = err as { status?: unknown; response?: { status?: unknown } } | null;
  const status = e?.response?.status ?? e?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

/**
 * 这个错误是不是「资源不存在」（404）。
 *
 * 同样要认两种形状，理由与 `extractApiErrorMessage` 一致：axios 拦截器把带
 * typed-error envelope 的 404 翻译成一个**没有 `.response`** 的 Error 子类，状态码
 * 挪到了 `err.status` 上（见 `services/errors.ts` 的 default 分支）；没有 envelope
 * 的 404 则还是原始 axios error。只认 `err.response.status` 会漏掉前者 —— 而后端
 * 的业务异常处理器**默认**产出 envelope，所以漏掉的恰恰是常见那一种。
 *
 * 用途：调用方需要把 404 与其它失败区别对待时（例如"这个对账组已经被删了，
 * 把界面上那份过期信息清掉"，而不是笼统地弹一句请求失败）。
 */
export function isNotFoundError(err: unknown): boolean {
  const e = err as { status?: unknown; response?: { status?: unknown } };
  return e?.response?.status === 404 || e?.status === 404;
}

/**
 * 这个错误是不是「没有权限」（401 / 403）。
 *
 * 两种形状都要认，理由同 `isNotFoundError`：带 envelope 的错误被 axios 拦截器
 * 翻译成**没有 `.response`** 的 Error 子类，状态码挪到了 `err.status` 上。
 *
 * 用途：列表页把"无权限"与"没有数据"分开说。一个账本页面把 403 渲染成
 * 「暂无流水」，用户读到的是"账上没有钱" —— 那是这一类页面最危险的误读。
 * 401 一起算：拦截器会去刷新 token / 触发登出，但在那之前这一屏同样不该
 * 谎称"没有数据"。
 */
export function isForbiddenError(err: unknown): boolean {
  const e = err as { status?: unknown; response?: { status?: unknown } };
  const status = e?.response?.status ?? e?.status;
  return status === 403 || status === 401;
}

/**
 * 取后端稳定的业务错误码（大写），取不到返回 null。
 *
 * 两种形状都要认，理由同上：
 *  1. 带 envelope 的 4xx 被 axios 拦截器翻译成 typed Error —— 码在 `err.code`
 *     （`services/errors.ts` 的 default 分支写入，**没有 `.response`**）；
 *  2. 未被翻译的原始 axios error —— 码在 `err.response.data.error_code`
 *     （新契约，大写）或 `err.response.data.error`（历史契约，小写）。
 *
 * 先看 envelope 再看 `err.code`：原始 AxiosError 自己也有 `.code`
 * （`ERR_BAD_REQUEST` / `ERR_NETWORK`），先读它会把 envelope 里真正的业务码盖掉。
 *
 * 注：`features/intake/apiError.ts#getApiErrorCode` 与
 * `components/upsellRequests/RefundSection.tsx#extractApiErrorCode` 是先落地的
 * 等价实现；共享组件层（如 message-board）不能反向 import 功能目录，统一从这里取。
 */
export function extractApiErrorCode(err: unknown): string | null {
  const e = err as {
    code?: unknown;
    response?: { data?: { error_code?: unknown; error?: unknown } };
  } | null;
  const data = e?.response?.data;
  const candidates = [data?.error_code, data?.error, e?.code];
  for (const raw of candidates) {
    if (typeof raw === 'string' && raw.trim()) return raw.trim().toUpperCase();
  }
  return null;
}

/** `refund_reason_code` → `refundReasonCode`（后端字段名是 snake_case，表单键是 camelCase）。 */
function toCamel(field: string): string {
  return field.replace(/_([a-z0-9])/g, (_m, c: string) => c.toUpperCase());
}

/**
 * 从错误里取**出错的字段名**，用于高亮具体输入框。
 *
 * 后端两条路都带字段信息，形状不同：
 *  1. **400 业务校验**（`ValidationError(message=..., field="amount")`）
 *     → envelope `details = { field: "amount" }`（snake_case 参数名）
 *  2. **422 Pydantic**（`ValidationExceptionHandler`）
 *     → envelope `details = [{ field: "body.transactionDate", message, type }]`
 *  3. FastAPI 原生 422（未经本项目 handler）→ `detail = [{ loc: ["body","amount"] }]`
 *
 * 取值路径同样有两条：拦截器把 400/422 翻译成**没有 `.response`** 的 Error 子类，
 * details 挂在 `err.details` 上（见 `services/errors.ts` 的 default 分支）；未被
 * 翻译的错误则还在 `err.response.data` 里。两条都认。
 *
 * 返回 camelCase 字段名（`body.` / `query.` / `path.` 前缀已剥掉），取不到返回 null。
 */
export function extractApiErrorField(err: unknown): string | null {
  const e = err as {
    details?: unknown;
    response?: { data?: { details?: unknown; detail?: unknown } };
  };

  const candidates = [e?.details, e?.response?.data?.details, e?.response?.data?.detail];

  for (const candidate of candidates) {
    const first = Array.isArray(candidate) ? candidate[0] : candidate;
    if (!first || typeof first !== 'object') continue;

    const entry = first as { field?: unknown; loc?: unknown };
    let raw: string | null = null;

    if (typeof entry.field === 'string' && entry.field) {
      raw = entry.field;
    } else if (Array.isArray(entry.loc) && entry.loc.length > 0) {
      // FastAPI 原生：loc = ["body", "amount"]，末段才是字段名。
      raw = String(entry.loc[entry.loc.length - 1]);
    }
    if (!raw) continue;

    const last = raw.split('.').filter(Boolean).pop();
    if (!last || last === 'body' || last === 'query' || last === 'path') continue;
    return toCamel(last);
  }

  return null;
}
